/**
 * 그룹 통계 카드 컴포넌트
 * - 사용자 통계 (전체, 강사, 학생)
 * - 문제집 통계 (문제집 수, 전체 문제)
 * - 제출 통계 (총 제출, 정답률)
 * - 최근 활동 목록
 */

import React from 'react';
import { Card, Row, Col, Statistic, Timeline, Typography, Space, Empty } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { GroupStatistics as GroupStatisticsType } from '../../../types/group';

const { Text } = Typography;

interface GroupStatisticsProps {
  statistics: GroupStatisticsType;
  loading?: boolean;
}

export const GroupStatistics: React.FC<GroupStatisticsProps> = ({
  statistics,
  loading = false,
}) => {
  /**
   * 상대 시간 표시 (예: "5분 전", "1시간 전")
   */
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    return `${diffDays}일 전`;
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 사용자 통계 */}
      <Card title="👥 사용자 통계" loading={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="전체 사용자"
              value={statistics.total_users}
              suffix="명"
              prefix={<UserOutlined />}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              활성: {statistics.active_users}명 / 비활성: {statistics.inactive_users}명
            </Text>
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="강사"
              value={statistics.instructors}
              suffix="명"
              prefix={<TeamOutlined />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="학생"
              value={statistics.students}
              suffix="명"
              prefix={<TeamOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* 문제집 및 제출 통계 */}
      <Card title="📚 문제집 및 제출 통계" loading={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="문제집 수"
              value={statistics.workbooks_count}
              suffix="개"
              prefix={<BookOutlined />}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              전체 문제: {statistics.total_problems}개
            </Text>
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="총 제출"
              value={statistics.total_submissions}
              suffix="건"
              prefix={<CheckCircleOutlined />}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              채점 완료: {statistics.graded_submissions}건 / 대기: {statistics.pending_submissions}건
            </Text>
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="정답률"
              value={statistics.overall_accuracy}
              suffix="%"
              precision={1}
              valueStyle={{ color: statistics.overall_accuracy >= 70 ? '#3f8600' : '#cf1322' }}
            />
          </Col>
        </Row>
      </Card>

      {/* 최근 활동 */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>최근 활동</span>
          </Space>
        }
        loading={loading}
      >
        {statistics.recent_activities.length > 0 ? (
          <Timeline
            items={statistics.recent_activities.map((activity) => ({
              children: (
                <div>
                  <Text strong>{activity.user_name}</Text>
                  <Text> {activity.activity}</Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {getRelativeTime(activity.timestamp)}
                    </Text>
                  </div>
                </div>
              ),
            }))}
          />
        ) : (
          <Empty
            description="최근 활동이 없습니다"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </Space>
  );
};
